/*----------------------------------
- COMPONENT: PUBLIC DOMAINS TABLE
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr ADR-0028
 *
 * The public listing's table, the same row the product shows.
 *
 * A real `<table>`, not a grid with roles: search engines and the retrievers that turn pages into
 * Markdown extract `<table>` elements, and this table is the page's evidence. A div grid would be
 * equivalent for assistive tech and invisible to them.
 *
 * There is no sort affordance here and no pagination. The order is the engine's daily shuffle, the
 * preview is bounded, and the Sort control in the status row opens authentication instead of
 * reordering: a query-string sort would also turn the page `noindex`.
 *
 * It wears the one table skin (`ud-table__*`, ADR-0072), the look every Product table now shares;
 * `publicDomainsTable.css` keeps only the real table's layout, the cells, the hover cards and the
 * veil. The footer is the Upside legend alone, centered: the "Showing N of M" line left on
 * 2026-10-04, and the count reads on the See all button below instead.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import Icon from '@/client/components/ui/icon/Icon';
import useTableScrollEdge from '@uniquedomains/components/table/useTableScrollEdge';
import { resolveTableCellValue } from '@uniquedomains/components/domains/table/cellValues';
import { pickCardVisual } from '@/client/catalogs/landing/domains/cardVisuals';
import {
    PUBLIC_TABLE_ACTIONS_WIDTH,
    PUBLIC_TABLE_CELL_GUTTER,
    PUBLIC_TABLE_EDGE_INSET,
    PUBLIC_TABLE_IDENTITY_WIDTH,
    type PublicDomainsTableColumn,
} from '@/client/catalogs/landing/domains/publicTableColumns';

import PublicDomainsTableCell from './PublicDomainsTableCell';
import PublicTableHoverCard from './PublicTableHoverCard';
import { CARD_ICONS, FILLED_ICON_INDEXES, splitDomainLabel } from './publicTableIdentity';

// Types
import type { TableCellRow } from '@uniquedomains/components/domains/table/cellValues';
import type { DomainsSearchTableRow } from '@uniquedomains/components/contracts/domains/search';
import { BROWSE_ESTIMATE_SPOKEN_LABEL } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';

import './publicDomainsTable.css';

/*----------------------------------
- TYPES
----------------------------------*/

export type PublicDomainsTableRow = {
    key: string;
    /**
     * The sanitized public row. Typed as the resolver's structural row rather than the private
     * `DomainsSearchRow`, so the public shape satisfies it without growing fields it never
     * publishes.
     */
    row: TableCellRow;
    projection: DomainsSearchTableRow | null;
};

type PublicDomainsTableProps = {
    rows: readonly PublicDomainsTableRow[];
    columns: readonly PublicDomainsTableColumn[];
    /** Per-column count of rows on this page carrying a reading. */
    fillCounts: Record<string, number>;
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" after a sampled total (ADR-0034 section 5). */
    countPrecision: DatabaseBrowseCountPrecision;
    /**
     * Every gated affordance hands off to the product through the same conversion path. Opening
     * a row names it, so the signup wall can answer with that domain rather than the list.
     */
    onOpenRow: (domain: string) => void;
    /** The row "+", naming the row too: its wall is the Watchlist gate, not See all. */
    onAddRow: (domain: string) => void;
    onSeeAll: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsTable = ({
    rows,
    columns,
    fillCounts,
    totalCountLabel,
    countPrecision,
    onOpenRow,
    onAddRow,
    onSeeAll,
}: PublicDomainsTableProps) => {
    const scrollerRef = React.useRef<HTMLDivElement | null>(null);
    useTableScrollEdge(scrollerRef);

    return (
        <div class="ud-table__shell ud-public-table-shell" data-reveal>
            <div ref={scrollerRef} class="ud-table__scroller ud-public-table-scroller">
                <table class="ud-table ud-public-table" aria-label="Domain results">
                    <colgroup>
                        <col
                            style={{
                                width: `${PUBLIC_TABLE_IDENTITY_WIDTH + PUBLIC_TABLE_EDGE_INSET + PUBLIC_TABLE_CELL_GUTTER}px`,
                            }}
                        />
                        {columns.map((column) => (
                            /* The catalog width is the content width the product's grid gives a track.
                               A `<col>` under `table-layout: fixed` sizes the border box instead, so
                               the cell's own horizontal padding would eat into it and wrap labels the
                               product fits on one line. Adding the gutter back keeps both tables
                               showing the same column at the same size. */
                            <col
                                key={column.id}
                                style={{ width: `${column.width + PUBLIC_TABLE_CELL_GUTTER * 2}px` }}
                            />
                        ))}
                        <col
                            style={{
                                width: `${PUBLIC_TABLE_ACTIONS_WIDTH + PUBLIC_TABLE_EDGE_INSET + PUBLIC_TABLE_CELL_GUTTER}px`,
                            }}
                        />
                    </colgroup>

                    <thead class="ud-table__head">
                        <tr>
                            <th scope="col" data-pinned="left" class="ud-table__header-cell ud-public-table__head is-identity">
                                Domain
                            </th>
                            {columns.map((column, index) => (
                                <th
                                    key={column.id}
                                    scope="col"
                                    data-column={column.id}
                                    data-align={column.align}
                                    class="ud-table__header-cell ud-public-table__head is-described"
                                    tabIndex={0}
                                    aria-describedby={`ud-col-${column.id}`}
                                >
                                    {column.label}
                                    <PublicTableHoverCard
                                        id={`ud-col-${column.id}`}
                                        variant="header"
                                        // The last columns sit against the scroller's right edge, so
                                        // their card hangs leftwards or it opens off-screen.
                                        align={index >= columns.length - 3 ? 'end' : 'start'}
                                        title={column.label}
                                        lines={[
                                            { key: 'What', value: column.provenance.what },
                                            { key: 'From', value: column.provenance.from },
                                            { key: 'Refreshed', value: column.provenance.refreshed },
                                            {
                                                key: 'On this page',
                                                value: `Filled on ${fillCounts[column.id] ?? 0} of ${rows.length} rows`,
                                            },
                                        ]}
                                    />
                                </th>
                            ))}
                            <th scope="col" data-pinned="right" class="ud-table__header-cell ud-public-table__head is-actions">
                                <span class="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map(({ key, row, projection }) => {
                            const visual = pickCardVisual(row.domain);
                            const rowIcon = CARD_ICONS[visual.iconIndex];
                            const usesFill = FILLED_ICON_INDEXES.includes(visual.iconIndex);
                            const label = splitDomainLabel(row.domain);

                            return (
                                <tr key={key} data-testid="domains-table-row" class="ud-table__row ud-public-table__row">
                                    <th scope="row" data-pinned="left" class="ud-table__cell ud-table__identity ud-public-table__identity">
                                        <span
                                            class="ud-public-table__tile"
                                            // Same 12% wash the cards used, so the identity a visitor
                                            // recognises survives the move to a table.
                                            style={{ background: `color-mix(in srgb, ${visual.color} 12%, #fff)` }}
                                        >
                                            <Icon
                                                name={rowIcon}
                                                size={15}
                                                color={visual.color}
                                                fill={usesFill ? visual.color : 'none'}
                                                strokeWidth={usesFill ? 0 : 2.1}
                                            />
                                        </span>
                                        <button
                                            type="button"
                                            class="ud-public-table__name"
                                            aria-label={`Open ${row.domain}`}
                                            // Wrapped: handed straight to onClick, the handler would
                                            // receive the click event where it expects the name.
                                            onClick={() => onOpenRow(row.domain)}
                                        >
                                            {label.name}
                                            {label.tld ? <span class="ud-public-table__tld">{label.tld}</span> : null}
                                        </button>
                                    </th>

                                    {columns.map((column) => {
                                        const value = resolveTableCellValue(column.id, row, projection);
                                        return (
                                            <td
                                                key={column.id}
                                                data-column={column.id}
                                                data-align={column.align}
                                                class="ud-table__cell ud-public-table__td"
                                                // Only cells with evidence are focusable: a tab stop
                                                // that reveals nothing is noise for keyboard readers.
                                                tabIndex={value.hover ? 0 : undefined}
                                            >
                                                <PublicDomainsTableCell value={value} align={column.align} />
                                            </td>
                                        );
                                    })}

                                    <td data-pinned="right" class="ud-table__cell ud-table__actions ud-public-table__td is-actions">
                                        <button
                                            type="button"
                                            class="ud-public-table__add"
                                            aria-label={`Add ${row.domain}`}
                                            title="Add to a watchlist"
                                            onClick={() => onAddRow(row.domain)}
                                        >
                                            <Icon name="plus" size={13} strokeWidth={2.4} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div class="ud-table__footer">
                <span class="ud-public-table-footer__legend">
                    <span class="ud-public-table-footer__legend-item">
                        <span class="ud-public-table__upside-marker is-price" aria-hidden="true" />
                        price
                    </span>
                    <span class="ud-public-table-footer__legend-item">
                        <span class="ud-public-table__upside-marker is-median" aria-hidden="true" />
                        similar sales median
                    </span>
                    <span class="ud-public-table-footer__legend-item">
                        <span class="ud-public-table__upside-marker is-estimate" aria-hidden="true" />
                        estimate
                    </span>
                </span>
            </div>

            <div class="ud-public-domains-see-all-wrap">
                <button type="button" onClick={onSeeAll} data-testid="domains-see-all" class="ud-public-domains-see-all">
                    <span>
                        See all
                        {totalCountLabel ? (
                            <>
                                {' '}
                                <strong>{totalCountLabel}</strong>
                                {countPrecision === 'sampled' ? <span class="sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                            </>
                        ) : null}{' '}
                        domains
                    </span>
                    <span class="ud-public-domains-see-all__arrow" aria-hidden="true">
                        <Icon name="arrow-right" size={15} strokeWidth={2.2} />
                    </span>
                </button>
            </div>
        </div>
    );
};

export default PublicDomainsTable;
